import { Button, Flex, Radio, Spin, Tooltip, type ModalProps } from 'antd'
import { isEmpty, uniqBy } from 'lodash-es'
import { FunctionComponent, useState, type ReactNode } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import styled from 'styled-components'
import { BasicModal } from '@/components/common/antd/basic-modal'
import CardErrorCosmos from '@/components/common/card/card-error-cosmos'
import { PostImageWrapper } from '@/components/common/image/post-image'
import {
  default as PostCard,
  PostCardContent,
  PostCardOtherInfo,
  PostCardTitle,
} from '@/components/common/post-card'
import PostCardStatistic from '@/components/common/post/post-card-statistic'
import PlatformSelector from '@/components/common/selectors/platform-selector'
import { NoData } from '@/components/kol/detail/no-data'
import useKolPosts from '@/components/kol/hooks/use-kol-posts'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Post } from '@/types/redux/kol-content'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import {
  convertPlatformType,
  type PlatformShortcode,
} from '@/utils/convert-platform'
import { platformShortcodeScoreCompareFn } from '@/utils/get-main-platforms'
import getPostThumbnailUrls from '@/utils/kol/get-post-thumbnail-urls'
import { isPlatformShortcode } from '@/utils/platform/is-platform-shortcode'

interface SelectKolPostModalProps extends Omit<ModalProps, 'width'> {
  initialSelectedPostIds?: string[]
  onSubmit: (posts: Post[]) => void
  kolInfo: KolSchemaModel
  maximumSelectedPostCount?: number
}

const SelectKolPostModal: FunctionComponent<SelectKolPostModalProps> = ({
  onSubmit,
  kolInfo,
  initialSelectedPostIds = [],
  maximumSelectedPostCount = 20,
  ...props
}) => {
  const { formatMessage, formatNumber } = useIntl()

  const platforms = kolInfo.kpi
    .map((kpi) => kpi.platform)
    .filter(isPlatformShortcode)
    .toSorted(platformShortcodeScoreCompareFn)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformShortcode>(
    platforms[0],
  )
  const [nextPagingToken, setNextPagingToken] = useState<string>()
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>(
    initialSelectedPostIds,
  )
  const [cachedPosts, setCachedPosts] = useState<Post[]>([])

  const kolId = kolInfo.id
  const isMaxPostCountExceeded =
    selectedPostIds.length > maximumSelectedPostCount

  const {
    response,
    isFetching: isFetchingPosts,
    isError: isErrorForFetchingPosts,
  } = useKolPosts({
    kolId,
    platform: selectedPlatform,
    request: {
      next_paging_token: nextPagingToken,
    },
  })
  const postList = response?.data ?? []
  const hasNextPage = !!response?.paging.next
  const [sentryRef] = useInfiniteScroll({
    onLoadMore: () => {
      setNextPagingToken(response?.paging.next)
    },
    loading: isFetchingPosts,
    disabled: isErrorForFetchingPosts,
    hasNextPage,
  })

  return (
    <BasicModal
      centered
      destroyOnClose
      cancelButtonProps={{ style: { display: 'none' } }}
      footer={() => {
        return (
          <Flex align='center' gap={16} justify='end'>
            <Tooltip
              title={
                isMaxPostCountExceeded
                  ? formatMessage(
                      {
                        id: 'select_kol_post_modal:post_list_section_limit',
                      },
                      {
                        count: formatNumber(maximumSelectedPostCount),
                      },
                    )
                  : undefined
              }
            >
              <Button
                disabled={isEmpty(selectedPostIds) || isMaxPostCountExceeded}
                type='primary'
                onClick={() => {
                  onSubmit(
                    uniqBy([...postList, ...cachedPosts], 'postID').filter(
                      (post) => selectedPostIds.includes(post.postID),
                    ),
                  )
                }}
              >
                {formatMessage({ id: 'general:submit_btn' })}
              </Button>
            </Tooltip>
          </Flex>
        )
      }}
      title={formatMessage({
        id: 'select_kol_post_modal:title',
      })}
      width={520}
      {...props}
    >
      <Container>
        <div>
          <Label>
            {formatMessage({ id: 'directory:search_field_type_platforms' })}
          </Label>
          <PlatformSelector
            showIcon
            availablePlatforms={platforms}
            size='small'
            value={selectedPlatform}
            onChange={(platform): void => {
              const currentSelectedPosts = postList.filter((post) =>
                selectedPostIds.includes(post.postID),
              )
              setCachedPosts((prev) =>
                uniqBy([...currentSelectedPosts, ...prev], 'postID'),
              )
              setNextPagingToken(undefined)
              setSelectedPlatform(platform)
            }}
          />
        </div>
        <div>
          <Label>
            {formatMessage({ id: 'select_kol_post_modal:post_list_section' })}
          </Label>
          <PostList>
            {((): ReactNode => {
              if (!isFetchingPosts && isEmpty(postList)) {
                return (
                  <Flex align='center' justify='center' style={{ flex: 1 }}>
                    <NoData />
                  </Flex>
                )
              }

              if (isErrorForFetchingPosts) {
                return <CardErrorCosmos size='small' />
              }

              return (
                <>
                  {postList.map((post) => {
                    const postPlatform = convertPlatformType(post.platformType)
                    const isChecked = selectedPostIds.includes(post.postID)

                    return (
                      <PostItem
                        checked={isChecked}
                        key={post.postID}
                        onClick={(): void => {
                          if (isChecked) {
                            setSelectedPostIds((prev) =>
                              prev.filter((p) => p !== post.postID),
                            )
                          } else {
                            setSelectedPostIds((prev) => [...prev, post.postID])
                          }
                        }}
                      >
                        <PostCard
                          canGiveFeedback={false}
                          commentCount={post.commentCount}
                          isDisableLike={post.isDisableLike}
                          kolName={kolInfo.name}
                          kolUuid={kolId}
                          likeCount={post.likeCount}
                          platformShortCode={postPlatform?.shortcode}
                          playCount={post.playCount}
                          postUrl={post.url}
                          publishedAt={post.publishTime}
                          shareCount={post.shareCount}
                          showKolInfo={false}
                          showPlatformIcon={false}
                          thumbnailUrls={getPostThumbnailUrls(post)}
                          title={post.title.trim() + post.text.trim()}
                          titleLineClamp={1}
                          viewCount={post.viewCount}
                        />
                      </PostItem>
                    )
                  })}
                  {(hasNextPage || isFetchingPosts) && (
                    <LoadingContainer ref={sentryRef}>
                      <Spin />
                    </LoadingContainer>
                  )}
                </>
              )
            })()}
          </PostList>
        </div>
      </Container>
    </BasicModal>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ant-select {
    width: 100%;
  }
`

const Label = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 40px;

  &::before {
    content: '* ';
    color: ${({ theme }): string => theme.colors.tagStatus.error};
  }
`

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 440px;
  overflow-y: auto;
`

const PostItem = styled(Radio)`
  width: 100%;
  height: 100px;
  display: flex;
  border-radius: 4px;
  border: 1px solid rgb(0, 0, 0, 0.09);
  margin: 0;

  .ant-radio {
    padding: 42px 22px;
  }

  span.ant-radio + * {
    flex: 1;
    padding-inline-start: 0;
    padding-inline-end: 0;
    overflow: hidden;
  }

  ${PostCard} {
    flex-direction: row;
    box-shadow: none;

    ${PostImageWrapper} {
      width: 98px;
      height: 98px;
    }

    ${PostCardContent} {
      border-radius: 3px;
      padding: 8px 16px;
      justify-content: center;
      gap: 8px;

      ${PostCardOtherInfo} {
        margin: 0;
      }

      ${PostCardTitle} {
        font-size: 14px;
        font-weight: 500;
        margin: 0;
      }

      ${PostCardStatistic} {
        margin: 0;
        flex-grow: 0;
      }
    }
  }

  &:hover,
  &.ant-radio-wrapper-checked {
    border: 1px solid ${({ theme }): string => theme.colors.brand.primary};
  }
`

const LoadingContainer = styled.div`
  height: 100%;

  .ant-spin {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
    height: 100%;
  }
`

export default SelectKolPostModal
